import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  AreaChart,
  Area,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import getColors, { createColorPalette } from '@/utils/colorPalettes';
import { ForecastRow } from '@/utils/newForecast';

interface TableRow {
  id: string;
  category: string;
  [key: string]: string | number;
}

interface DataPoint {
  year: number;
  id: string;
  categoryName: string;
  [key: string]: number | string;
}
interface ChartYearData {
  year: number;
  [categoryName: string]: number;
}

interface RevenueForecastChartProps {
  revenueData?: ForecastRow[];
  yoyData?: TableRow[];
  showYOY?: boolean;
  onYOYToggle?: (show: boolean) => void;
  baseYear?: number;
  loading?: boolean;
  title?: string;
  height?: number;
}

const createChartData = (data: ForecastRow[], baseYear: number): ChartYearData[] => {
  const chartData: ChartYearData[] = [];

  for (let i = -5; i <= 5; i++) {
    const year = baseYear + i;
    const yearData: ChartYearData = { year };
    data.forEach(row => {
      const revenue = row[i]?.displayRevenue || 0;
      yearData[row.categoryName] = revenue;
    });
    chartData.push(yearData);
  }

  return chartData;
};

const RevenueForecastChart: React.FC<RevenueForecastChartProps> = ({
  revenueData,
  yoyData,
  showYOY = false,
  onYOYToggle,
  baseYear = 2024,
  loading = false,
  title = 'Revenue Forecast',
  height = 450,
}) => {
  // Combine and transform data for the chart
  const chartData: ChartYearData[] = React.useMemo(() => {
    if (!revenueData || revenueData.length === 0) return [];

    const processedRows = createChartData(revenueData, baseYear);
    //console.log("Processed Rows:", processedRows);
    // Add historical data if available

    return processedRows;
  }, [revenueData, baseYear]);
  //console.log("Chart Data:", chartData);

  const colorPalette = getColors(revenueData.length);

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const isHistorical = label < baseYear;

      return (
        <Paper
          elevation={6}
          sx={{
            p: 2.5,
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            border: `2px solid ${isHistorical ? '#4caf50' : '#1976d2'}`,
            borderRadius: '12px',
            minWidth: '220px',
            maxHeight: '400px',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              color: isHistorical ? '#4caf50' : '#1976d2',
              mb: 1,
            }}
          >
            {label} {isHistorical ? '(Historical)' : '(Forecast)'}
          </Typography>
          {payload.map((entry: any, index: number) => {
            const row = revenueData.find(cat => cat.id === entry.dataKey);
            const categoryName = row.categoryName || entry.dataKey;
            const value = entry.value;
            const formattedValue = showYOY ? `${value.toFixed(2)}%` : `$${value.toLocaleString()}M`;

            return (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  color: '#333',
                  fontWeight: '500',
                  mb: 0.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <span style={{ color: entry.color, fontWeight: 'bold' }}>{categoryName}:</span>
                <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>
              </Typography>
            );
          })}
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, mt: 3, mb: 3 }}>
        <Box
          sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography variant="h6" color="text.secondary">
            Loading chart data...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!revenueData || revenueData.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, mt: 3, mb: 3 }}>
        <Box
          sx={{ height: height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography variant="h6" color="text.secondary">
            No data available
          </Typography>
        </Box>
      </Paper>
    );
  }
  console.log('colors', colorPalette);

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3, mb: 3, borderRadius: '16px' }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          mb: 3,
          fontWeight: 'bold',
          color: '#1976d2',
          textAlign: 'center',
        }}
      >
        Study Year Data
      </Typography>

      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeOpacity={0.7} />

          <XAxis
            dataKey="year"
            tick={{ fontSize: 12, fill: '#666' }}
            axisLine={{ stroke: '#ccc', strokeWidth: 1 }}
            tickLine={{ stroke: '#ccc' }}
          />

          <YAxis
            tick={{ fontSize: 12, fill: '#666' }}
            axisLine={{ stroke: '#ccc', strokeWidth: 1 }}
            tickLine={{ stroke: '#ccc' }}
            tickFormatter={value =>
              showYOY
                ? `${value.toFixed(0)}%`
                : value >= 1000
                  ? `$${(value / 1000).toFixed(1)}B`
                  : `$${value.toFixed(0)}M`
            }
            label={{
              value: showYOY ? 'YOY Growth (%)' : 'Revenue',
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#666', fontSize: '14px' },
            }}
          />

          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '13px',
            }}
          />

          {revenueData.map((row: ForecastRow, index) => (
            <Area
              key={row.categoryName}
              // type="monotone"
              dataKey={row.categoryName}
              stackId="1"
              stroke={colorPalette[index % colorPalette.length]}
              fill={colorPalette[index % colorPalette.length]}
              fillOpacity={0.6}
              strokeWidth={2}
              name={row.categoryName}
            />
          ))}

          <ReferenceLine
            x={baseYear}
            stroke="#ff0000"
            strokeDasharray="5 5"
            strokeWidth={2}
            label={{
              value: 'Forecast Start',
              position: 'top',
              style: { fill: '#ff0000', fontSize: '12px' },
            }}
          />

          {showYOY && <ReferenceLine y={0} stroke="#999" strokeDasharray="2 2" strokeWidth={1} />}
        </AreaChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default RevenueForecastChart;
